import { Alert, Autocomplete, Box, Grid2, Stack, TextField, Typography } from '@mui/material'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Application_Type,
  Criteria_Group_Sorting_Config,
  Criteria_Group_Sorting_Config_Bool_Exp,
  Criteria_Sorting_Config,
  Criteria_Sorting_Config_Bool_Exp,
  Query_Root,
} from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { fetchProcessCriteriaSorted } from 'src/screens/administration/configuration-management/criteria-configuration/criteriaConfigQueries'
import { useSortCriterias } from 'src/screens/shared/configuration/criteria/CriteriaConfigurationSortingUtils'
import { CriteriaGroupAndCriteriaSortList } from 'src/screens/shared/configuration/criteria/CriteriaGroupAndCriteriaSortList'
import { PROCESS, PROJECT_TYPE } from 'src/shared/constants/constants'
import { Option } from 'src/shared/form/control'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'
import { debounce } from 'lodash'
import { getCriteriaTypeOptions } from 'src/screens/administration/configuration-management/criteria-configuration/CriteriaConfigUtils'
import {
  SpecificConfigFilter,
  useFilterSettingsFromLocalStorage,
} from 'src/screens/administration/configuration-management/ConfigurationStorageUtils'
import {
  fetchApplicationTypesByProcesses,
  mapApplicationTypesToOptions,
} from 'src/screens/administration/configuration-management/criteria-configuration/evaluation-criteria/EvaluationCriteriaGridUtils'
import {
  getTranslationForApplicationType,
  getTranslationForCriteriaType,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'

type CRITERIA_PROJECT_TYPE = Extract<PROJECT_TYPE, 'PF_KAP' | 'PF_PGV'>

type CriteriaProjectFilters = SpecificConfigFilter<'CRITERIA_PF_KAP'> | SpecificConfigFilter<'CRITERIA_PF_PGV'>

type Props = {
  projectType: CRITERIA_PROJECT_TYPE
}

export const CriteriaConfigurationProject = ({ projectType }: Props): ReactElement => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const { switchCriteriaGroupsPosition, switchCriteriasPosition } = useSortCriterias()

  // fetched filter data - application types
  const [applicationTypesOfProcess, setApplicationTypesOfProcess] = useState<Application_Type[]>([])

  // sort list state
  const [criteriaGroupsSorting, setCriteriaGroupsSorting] = useState<Criteria_Group_Sorting_Config[]>([])
  const [isReorderingLocked, setIsReorderingLocked] = useState(false)

  // filter options
  const applicationTypeOptions: Option[] = useMemo(() => {
    return mapApplicationTypesToOptions(applicationTypesOfProcess)
  }, [applicationTypesOfProcess])

  const criteriaTypeOptions: Option[] = useMemo(() => getCriteriaTypeOptions(getMessage), [getMessage])

  const defaultCriteriaProjectFiltersValues: CriteriaProjectFilters = useMemo(() => {
    switch (projectType) {
      case 'PF_KAP':
        return {
          criteriaPfKapApplicationTypeFilter: applicationTypeOptions[0] ?? null,
          criteriaPfKapCriteriaTypeFilter: criteriaTypeOptions[0] ?? null,
        }
      case 'PF_PGV':
        return {
          criteriaPfPgvApplicationTypeFilter: applicationTypeOptions[0] ?? null,
          criteriaPfPgvCriteriaTypeFilter: criteriaTypeOptions[0] ?? null,
        }
    }
  }, [applicationTypeOptions, criteriaTypeOptions, projectType])

  // sort list filter settings
  const [criteriaProjectFilters, setCriteriaProjectFilters] = useFilterSettingsFromLocalStorage(
    defaultCriteriaProjectFiltersValues,
  )

  const getProjectApplicationTypeFilter = useCallback(
    (filters: CriteriaProjectFilters, projectType: CRITERIA_PROJECT_TYPE) => {
      switch (projectType) {
        case PROCESS.PF_KAP:
          return (filters as SpecificConfigFilter<'CRITERIA_PF_KAP'>).criteriaPfKapApplicationTypeFilter
        case PROCESS.PF_PGV:
          return (filters as SpecificConfigFilter<'CRITERIA_PF_PGV'>).criteriaPfPgvApplicationTypeFilter
      }
    },
    [],
  )

  const getProjectCriteriaTypeFilter = useCallback(
    (filters: CriteriaProjectFilters, projectType: CRITERIA_PROJECT_TYPE) => {
      switch (projectType) {
        case PROCESS.PF_KAP:
          return (filters as SpecificConfigFilter<'CRITERIA_PF_KAP'>).criteriaPfKapCriteriaTypeFilter
        case PROCESS.PF_PGV:
          return (filters as SpecificConfigFilter<'CRITERIA_PF_PGV'>).criteriaPfPgvCriteriaTypeFilter
      }
    },
    [],
  )

  // fetch data
  const fetchCriterias = useCallback(async () => {
    if (getProjectApplicationTypeFilter(criteriaProjectFilters, projectType)?.value) {
      const criteriaGroupWhereClause: Criteria_Group_Sorting_Config_Bool_Exp = {
        _and: [
          { criteria_type: { _eq: getProjectCriteriaTypeFilter(criteriaProjectFilters, projectType).value as string } },
          { process: { _eq: projectType } },
          {
            application_type_id: {
              _eq: getProjectApplicationTypeFilter(criteriaProjectFilters, projectType).value as number,
            },
          },
        ],
      }

      const criteriaWhereClause: Criteria_Sorting_Config_Bool_Exp = {
        _and: [
          { criteria_type: { _eq: getProjectCriteriaTypeFilter(criteriaProjectFilters, projectType).value as string } },
          { process: { _eq: projectType } },
          {
            application_type_id: {
              _eq: getProjectApplicationTypeFilter(criteriaProjectFilters, projectType).value as number,
            },
          },
        ],
      }

      const { data, error } = await urqlClient
        .query<
          { criteria_group_sorting_config: Query_Root['criteria_group_sorting_config'] },
          {
            criteriaGroupWhereClause: Criteria_Group_Sorting_Config_Bool_Exp
            criteriaWhereClause: Criteria_Sorting_Config_Bool_Exp
          }
        >(fetchProcessCriteriaSorted, {
          criteriaGroupWhereClause: criteriaGroupWhereClause,
          criteriaWhereClause: criteriaWhereClause,
        })
        .toPromise()

      if (!error && data && data.criteria_group_sorting_config) {
        const criteriaGroupsWithCriterias = data.criteria_group_sorting_config.filter(
          (item) => item.criteria_group_config.criteria_sorting_configs.length > 0,
        )
        setCriteriaGroupsSorting(criteriaGroupsWithCriterias)
      } else {
        notificationService.operationFailed()
      }

      setIsReorderingLocked(false)
    }
  }, [
    criteriaProjectFilters,
    getProjectApplicationTypeFilter,
    getProjectCriteriaTypeFilter,
    notificationService,
    projectType,
    urqlClient,
  ])

  useEffect(() => {
    fetchApplicationTypesByProcesses(urqlClient)([projectType])
      .then((res) => {
        setApplicationTypesOfProcess(res.fetchedApplicationTypes)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [notificationService, projectType, urqlClient])

  useEffect(() => {
    if (getProjectApplicationTypeFilter(criteriaProjectFilters, projectType) == null) {
      setCriteriaProjectFilters(defaultCriteriaProjectFiltersValues)
    }
  }, [
    criteriaProjectFilters,
    defaultCriteriaProjectFiltersValues,
    getProjectApplicationTypeFilter,
    projectType,
    setCriteriaProjectFilters,
  ])

  useEffect(() => {
    fetchCriterias()
  }, [fetchCriterias])

  // handlers
  const handleOnApplicationTypeChange = useCallback(
    (value: Option) => {
      if (projectType === PROCESS.PF_KAP) {
        setCriteriaProjectFilters({
          ...criteriaProjectFilters,
          criteriaPfKapApplicationTypeFilter: value,
        })
      }
      if (projectType === PROCESS.PF_PGV) {
        setCriteriaProjectFilters({
          ...criteriaProjectFilters,
          criteriaPfPgvApplicationTypeFilter: value,
        })
      }
    },
    [criteriaProjectFilters, projectType, setCriteriaProjectFilters],
  )

  const onCriteriaTypeFilterChange = useCallback(
    (newValue: Option) => {
      if (projectType === PROCESS.PF_KAP) {
        setCriteriaProjectFilters({
          ...criteriaProjectFilters,
          criteriaPfKapCriteriaTypeFilter: newValue,
        })
      }
      if (projectType === PROCESS.PF_PGV) {
        setCriteriaProjectFilters({
          ...criteriaProjectFilters,
          criteriaPfPgvCriteriaTypeFilter: newValue,
        })
      }
    },
    [criteriaProjectFilters, projectType, setCriteriaProjectFilters],
  )

  const debouncedHandleReorder = useMemo(
    () =>
      debounce(
        async (reorderFunction: (...args: any[]) => Promise<void>, ...args: any[]) => {
          if (isReorderingLocked) return

          setIsReorderingLocked(true)

          try {
            await reorderFunction(...args)
            await new Promise((resolve) => setTimeout(resolve, 500))
          } catch (e) {
            setIsReorderingLocked(false)
            console.error('Error caught in criteria project sort debounce handler reorder method')
          }
        },
        500,
        { leading: true, trailing: false },
      ),
    [],
  )

  const onSwitchCriteriaGroupPositionInternal = useCallback(
    async (cg1: Criteria_Group_Sorting_Config, cg2: Criteria_Group_Sorting_Config) => {
      try {
        await switchCriteriaGroupsPosition(cg1, cg2, projectType)
        await fetchCriterias()
      } catch (e) {
        notificationService.operationFailed()
        throw e
      }
    },
    [fetchCriterias, notificationService, projectType, switchCriteriaGroupsPosition],
  )

  const onSwitchCriteriaPositionInternal = useCallback(
    async (c1: Criteria_Sorting_Config, c2: Criteria_Sorting_Config) => {
      try {
        await switchCriteriasPosition(c1, c2, projectType)
        await fetchCriterias()
      } catch (e) {
        notificationService.operationFailed()
        throw e
      }
    },
    [fetchCriterias, notificationService, projectType, switchCriteriasPosition],
  )

  const onSwitchCriteriaGroupPosition = useCallback(
    async (cg1: Criteria_Group_Sorting_Config, cg2: Criteria_Group_Sorting_Config) => {
      await debouncedHandleReorder(onSwitchCriteriaGroupPositionInternal, cg1, cg2)
    },
    [debouncedHandleReorder, onSwitchCriteriaGroupPositionInternal],
  )

  const onSwitchCriteriaPosition = useCallback(
    async (c1: Criteria_Sorting_Config, c2: Criteria_Sorting_Config) => {
      await debouncedHandleReorder(onSwitchCriteriaPositionInternal, c1, c2)
    },
    [debouncedHandleReorder, onSwitchCriteriaPositionInternal],
  )

  return (
    <ScreenLayout
      title={`${getMessage('label.navigation.configuration.criteria')} ${getMessage('label.configuration')}`}
    >
      <PageLayout>
        <Stack spacing={2}>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 1,
              padding: 2,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="body1" color="text.secondary" fontWeight="bold">
                {getMessage(`label.configuration.criteria.preview.settings`)}
              </Typography>
              <Grid2 container spacing={2}>
                <Grid2 size={6}>
                  <Autocomplete
                    key={applicationTypeOptions.length}
                    options={applicationTypeOptions}
                    value={getProjectApplicationTypeFilter(criteriaProjectFilters, projectType)}
                    onChange={(_, newVal) => handleOnApplicationTypeChange(newVal)}
                    getOptionLabel={(opt) =>
                      getTranslationForApplicationType(getMessage, opt.value, applicationTypeOptions)
                    }
                    disableClearable
                    renderInput={(params) => <TextField {...params} label={getMessage('label.application_type')} />}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <Autocomplete
                    options={criteriaTypeOptions}
                    value={getProjectCriteriaTypeFilter(criteriaProjectFilters, projectType)}
                    onChange={(_, newVal) => onCriteriaTypeFilterChange(newVal)}
                    getOptionLabel={(opt) => getTranslationForCriteriaType(getMessage, opt.value)}
                    disableClearable
                    renderInput={(params) => <TextField {...params} label={getMessage('label.criteria_type')} />}
                  />
                </Grid2>
              </Grid2>
            </Stack>
          </Box>
          <Box>
            <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>
          </Box>
          <Stack spacing={5}>
            <CriteriaGroupAndCriteriaSortList
              criteriaGroupsSorting={criteriaGroupsSorting}
              onSwitchCriteriaGroupPosition={onSwitchCriteriaGroupPosition}
              onSwitchCriteriaPosition={onSwitchCriteriaPosition}
              isReorderingLocked={isReorderingLocked}
              processType={projectType}
            />
          </Stack>
        </Stack>
      </PageLayout>
    </ScreenLayout>
  )
}
